import { useState, useEffect } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Package, Upload, LogOut, Menu, X, Home, ClipboardList, Mail, LayoutDashboard, Users, Tag, Activity, Settings, Star, Truck, PackageCheck, BookOpen, ImageIcon, Bell, Search, Megaphone, Link2, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";

const AdminLayout = () => {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;

    const checkAdmin = async (userId: string) => {
      const { data } = await supabase.rpc('has_role', { _user_id: userId, _role: 'admin' });
      return data === true;
    };

    const applySession = (session: any) => {
      if (!mounted) return;
      const currentUser = session?.user ?? null;
      setUser(currentUser);
      if (currentUser) {
        // Defer the supabase call to avoid deadlock inside onAuthStateChange
        setTimeout(async () => {
          const admin = await checkAdmin(currentUser.id);
          if (mounted) {
            setIsAdmin(admin);
            setLoading(false);
          }
        }, 0);
      } else {
        setIsAdmin(false);
        setLoading(false);
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      applySession(session);
    });

    supabase.auth.getSession().then(({ data: { session } }) => applySession(session));

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!loading && !user) {
      navigate("/admin/login");
    }
  }, [loading, user, navigate]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/admin/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading…</div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="text-center max-w-md">
          <h1 className="font-display text-2xl font-bold mb-2">Access Denied</h1>
          <p className="text-muted-foreground font-body mb-6">
            Your account does not have admin privileges.
          </p>
          <div className="flex gap-3 justify-center">
            <Button variant="outline" onClick={() => navigate("/")}>Go to Store</Button>
            <Button variant="outline" onClick={handleLogout}>Sign Out</Button>
          </div>
        </div>
      </div>
    );
  }

  const navItems = [
    { path: "/admin", label: "Dashboard", icon: LayoutDashboard },
    { path: "/admin/products", label: "Products", icon: Package },
    { path: "/admin/orders", label: "Orders", icon: ClipboardList },
    { path: "/admin/customers", label: "Customers", icon: Users },
    { path: "/admin/discounts", label: "Discounts", icon: Tag },
    { path: "/admin/subscribers", label: "Subscribers", icon: Mail },
    { path: "/admin/notifications", label: "Back in Stock", icon: Bell },
    { path: "/admin/reviews", label: "Reviews", icon: Star },
    { path: "/admin/shipping", label: "Shipping", icon: Truck },
    { path: "/admin/royal-mail", label: "Royal Mail", icon: PackageCheck },
    { path: "/admin/blog", label: "Blog", icon: BookOpen },
    { path: "/admin/blog-automation", label: "Blog Automation", icon: Zap },
    { path: "/admin/ad-banners", label: "Ad Banners", icon: Megaphone },
    { path: "/admin/affiliates", label: "Affiliates", icon: Link2 },
    { path: "/admin/seo-keywords", label: "SEO Keywords", icon: Search },
    { path: "/admin/images", label: "Image Migration", icon: ImageIcon },
    { path: "/admin/import", label: "Bulk Import", icon: Upload },
    { path: "/admin/activity", label: "Activity Log", icon: Activity },
    { path: "/admin/settings", label: "Settings", icon: Settings },
  ];

  return (
    <div className="min-h-screen flex bg-secondary/30">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-card border-r border-border transform transition-transform lg:translate-x-0 lg:static flex flex-col ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="p-6 border-b border-border shrink-0">
          <Link to="/" className="font-display text-xl font-bold text-primary">
            PlymHair
          </Link>
          <p className="text-xs text-muted-foreground font-body mt-1">Admin Dashboard</p>
        </div>

        <nav className="p-4 space-y-1 overflow-y-auto flex-1 min-h-0">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-4 py-2.5 rounded text-sm font-body transition-colors ${
                location.pathname === item.path
                  ? "bg-primary text-primary-foreground"
                  : "text-foreground hover:bg-secondary"
              }`}
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="p-4 border-t border-border space-y-2 shrink-0">
          <Link
            to="/"
            className="flex items-center gap-3 px-4 py-2.5 rounded text-sm font-body text-foreground hover:bg-secondary transition-colors w-full"
          >
            <Home className="w-4 h-4" />
            View Site
          </Link>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-2.5 rounded text-sm font-body text-destructive hover:bg-destructive/10 transition-colors w-full"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-foreground/40 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 bg-card border-b border-border flex items-center px-4 lg:px-6 gap-4">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden"
          >
            <Menu className="w-5 h-5" />
          </button>
          <p className="text-sm font-body text-muted-foreground">
            {user.email}
          </p>
        </header>

        <main className="flex-1 p-4 lg:p-6 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
